import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminLegacyUnsettledStoresQuery,
  AdminGetSellerSettlementInfoQuery,
  AdminGetSellerSettlementInfoQueryVariables,
  AdminUpdateSellerSettlementInfoMutation,
  AdminUpdateSellerSettlementInfoMutationVariables,
} from "../../../__generated__/graphql";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const ADMIN_LEGACY_UNSETTLED_STORES = gql(`
  query AdminLegacyUnsettledStoresForEdit {
    adminLegacyUnsettledStores {
      sellerId
      storeId
      storeTitle
      storeAddress
      sellerName
      contactNumber
      businessNumber
      bank
      accountHolder
      accountNumber
      taxationType
      canIssueInvoice
      isSettleInfoComplete
    }
  }
`);

const ADMIN_GET_SELLER_SETTLEMENT_INFO = gql(`
  query AdminGetSellerSettlementInfo($sellerId: Int!) {
    adminGetSellerSettlementInfo(sellerId: $sellerId) {
      sellerId
      storeId
      storeTitle
      sellerName
      contactNumber
      bank
      accountHolder
      accountNumber
      businessNumber
      taxationType
      canIssueInvoice
    }
  }
`);

const ADMIN_UPDATE_SELLER_SETTLEMENT_INFO = gql(`
  mutation AdminUpdateSellerSettlementInfo(
    $sellerId: Int!
    $bank: String
    $accountHolder: String
    $accountNumber: String
    $businessNumber: String
    $taxationType: String
    $canIssueInvoice: Boolean
  ) {
    adminUpdateSellerSettlementInfo(
      sellerId: $sellerId
      bank: $bank
      accountHolder: $accountHolder
      accountNumber: $accountNumber
      businessNumber: $businessNumber
      taxationType: $taxationType
      canIssueInvoice: $canIssueInvoice
    ) {
      ok
      error
    }
  }
`);

const TAX_TYPE_OPTIONS = ["일반과세자", "간이과세자", "면세사업자"] as const;

interface SettlementForm {
  bank: string;
  accountHolder: string;
  accountNumber: string;
  businessNumber: string;
  taxationType: string;
  canIssueInvoice: boolean;
}

function createFormFromInfo(
  info: NonNullable<
    AdminGetSellerSettlementInfoQuery["adminGetSellerSettlementInfo"]
  >,
): SettlementForm {
  return {
    bank: info.bank ?? "",
    accountHolder: info.accountHolder ?? "",
    accountNumber: info.accountNumber ?? "",
    businessNumber: info.businessNumber ?? "",
    taxationType: info.taxationType ?? "",
    canIssueInvoice: info.canIssueInvoice ?? false,
  };
}

export default function AdminLegacySettlementInfoEdit() {
  const [keyword, setKeyword] = useState("");
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null);
  const [form, setForm] = useState<SettlementForm | null>(null);

  const {
    data: storesData,
    loading: storesLoading,
    error: storesError,
    refetch: refetchStores,
  } = useQuery<AdminLegacyUnsettledStoresQuery>(ADMIN_LEGACY_UNSETTLED_STORES, {
    onError: (error) => {
      notifyError(error.message ?? "매장 목록 조회 중 오류가 발생했습니다.");
    },
  });

  const { loading: infoLoading, refetch: refetchInfo } = useQuery<
    AdminGetSellerSettlementInfoQuery,
    AdminGetSellerSettlementInfoQueryVariables
  >(ADMIN_GET_SELLER_SETTLEMENT_INFO, {
    variables: { sellerId: selectedSellerId ?? 0 },
    skip: selectedSellerId == null,
    onCompleted: (data) => {
      if (data.adminGetSellerSettlementInfo) {
        setForm(createFormFromInfo(data.adminGetSellerSettlementInfo));
      }
    },
    onError: (error) => {
      notifyError(error.message ?? "정산 정보 조회 중 오류가 발생했습니다.");
    },
  });

  const [updateSettlementInfo, { loading: updating }] = useMutation<
    AdminUpdateSellerSettlementInfoMutation,
    AdminUpdateSellerSettlementInfoMutationVariables
  >(ADMIN_UPDATE_SELLER_SETTLEMENT_INFO, {
    onCompleted: async (result) => {
      if (result.adminUpdateSellerSettlementInfo.ok) {
        notify("정산 정보가 수정되었습니다.");
        await refetchStores();
        if (selectedSellerId != null) {
          await refetchInfo({ sellerId: selectedSellerId });
        }
        return;
      }
      notifyError(
        result.adminUpdateSellerSettlementInfo.error ??
          "정산 정보 수정 중 오류가 발생했습니다.",
      );
    },
    onError: (error) => {
      notifyError(error.message ?? "정산 정보 수정 중 오류가 발생했습니다.");
    },
  });

  const stores = useMemo(
    () => storesData?.adminLegacyUnsettledStores ?? [],
    [storesData],
  );

  const filteredStores = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((store) =>
      [store.storeTitle, store.storeAddress ?? "", store.sellerName ?? "", store.contactNumber]
        .some((f) => f.toLowerCase().includes(q)),
    );
  }, [keyword, stores]);

  const selectedStore = useMemo(
    () =>
      filteredStores.find((s) => s.sellerId === selectedSellerId) ??
      stores.find((s) => s.sellerId === selectedSellerId) ??
      null,
    [filteredStores, selectedSellerId, stores],
  );

  useScrollToDetailPanelOnSelect(selectedStore);

  const handleFieldChange = (field: keyof SettlementForm, value: string | boolean) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmit = async () => {
    if (!selectedSellerId || !form) return;

    if (!form.bank.trim()) {
      notifyError("은행명을 입력해주세요.");
      return;
    }
    if (!form.accountHolder.trim()) {
      notifyError("예금주를 입력해주세요.");
      return;
    }
    if (!form.accountNumber.trim()) {
      notifyError("계좌번호를 입력해주세요.");
      return;
    }

    if (
      !window.confirm(
        `${selectedStore?.storeTitle ?? "선택한 매장"}의 정산 정보를 수정할까요?`,
      )
    ) {
      return;
    }

    await updateSettlementInfo({
      variables: {
        sellerId: selectedSellerId,
        bank: form.bank.trim(),
        accountHolder: form.accountHolder.trim(),
        accountNumber: form.accountNumber.trim(),
        businessNumber: form.businessNumber.trim() || null,
        taxationType: form.taxationType || null,
        canIssueInvoice: form.canIssueInvoice,
      },
    });
  };

  if (storesLoading && !storesData) {
    return (
      <div className="flex w-full justify-center p-8">
        <LoadingSpinnerInline />
      </div>
    );
  }

  if (storesError && !storesData) {
    return (
      <div className="w-full p-8 text-red-600">
        매장 목록을 불러오지 못했습니다.
      </div>
    );
  }

  return (
    <div className="w-full md:p-8">
      <AdminListDetailLayout
        title="레거시 정산 정보 수정"
        description="레거시 미정산 매장의 정산 정보(계좌·사업자)를 어드민에서 직접 수정합니다."
        leftRatio={7}
        rightRatio={3}
        leftPanel={{
          title: `매장 목록 (${filteredStores.length}곳)`,
          searchPlaceholder: "매장명, 주소, 사장님명, 연락처로 검색",
          searchValue: keyword,
          onSearchChange: setKeyword,
          children: (
            <AdminStoreTableWithMobileCards
              stores={filteredStores.map((store) => ({
                id: store.storeId,
                title: store.storeTitle,
                address: store.storeAddress,
              }))}
              selectionMode="single"
              selectedIds={selectedStore ? [selectedStore.storeId] : []}
              onSelect={(store) => {
                const matched = filteredStores.find((r) => r.storeId === store.id);
                setSelectedSellerId(matched?.sellerId ?? null);
                setForm(null);
              }}
              showImage={false}
              showAddress={false}
              extraHeaders={
                <>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">사장님</th>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">은행</th>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">예금주</th>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">계좌번호</th>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">사업자번호</th>
                  <th className="break-keep px-4 py-2 font-medium text-gray-700">정산 정보</th>
                </>
              }
              renderExtraCells={(store) => {
                const matched = filteredStores.find((r) => r.storeId === store.id);
                if (!matched) return null;

                return (
                  <>
                    <td className="break-keep px-4 py-3 text-sm text-gray-700">
                      {matched.sellerName ?? matched.contactNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matched.bank ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matched.accountHolder ?? "-"}
                    </td>
                    <td className="break-all px-4 py-3 text-sm text-gray-700">
                      {matched.accountNumber ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {matched.businessNumber ?? "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          matched.isSettleInfoComplete
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {matched.isSettleInfoComplete ? "완료" : "미작성"}
                      </span>
                    </td>
                  </>
                );
              }}
              renderExtraCard={(store) => {
                const matched = filteredStores.find((r) => r.storeId === store.id);
                if (!matched) return null;

                return (
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <div>사장님: {matched.sellerName ?? matched.contactNumber}</div>
                    <div>은행: {matched.bank ?? "-"}</div>
                    <div>계좌번호: {matched.accountNumber ?? "-"}</div>
                    <div>사업자번호: {matched.businessNumber ?? "-"}</div>
                  </div>
                );
              }}
              emptyText={
                keyword.trim() ? "검색 결과가 없습니다." : "대상 매장이 없습니다."
              }
            />
          ),
        }}
        rightPanel={{
          title: "정산 정보 수정",
          emptyText: "왼쪽 목록에서 매장을 선택하세요.",
          children:
            selectedStore && selectedSellerId != null ? (
              infoLoading || !form ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinnerInline />
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                    <div className="font-semibold text-gray-900">
                      {selectedStore.storeTitle}
                    </div>
                    <div>
                      사장님: {selectedStore.sellerName ?? selectedStore.contactNumber}
                    </div>
                    <div>연락처: {selectedStore.contactNumber}</div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900">계좌 정보</h3>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        은행
                      </label>
                      <input
                        type="text"
                        value={form.bank}
                        onChange={(e) => handleFieldChange("bank", e.target.value)}
                        placeholder="예: 국민은행"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        예금주
                      </label>
                      <input
                        type="text"
                        value={form.accountHolder}
                        onChange={(e) =>
                          handleFieldChange("accountHolder", e.target.value)
                        }
                        placeholder="예금주명"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        계좌번호
                      </label>
                      <input
                        type="text"
                        value={form.accountNumber}
                        onChange={(e) =>
                          handleFieldChange("accountNumber", e.target.value)
                        }
                        placeholder="계좌번호"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900">사업자 정보</h3>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        사업자번호
                      </label>
                      <input
                        type="text"
                        value={form.businessNumber}
                        onChange={(e) =>
                          handleFieldChange("businessNumber", e.target.value)
                        }
                        placeholder="사업자번호 (10자리)"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        과세 유형
                      </label>
                      <select
                        value={form.taxationType}
                        onChange={(e) =>
                          handleFieldChange("taxationType", e.target.value)
                        }
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">선택하세요</option>
                        {TAX_TYPE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="canIssueInvoice"
                        checked={form.canIssueInvoice}
                        onChange={(e) =>
                          handleFieldChange("canIssueInvoice", e.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <label
                        htmlFor="canIssueInvoice"
                        className="text-sm text-gray-700"
                      >
                        세금계산서 발행 가능
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={updating}
                    className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {updating ? "수정 중..." : "정산 정보 저장"}
                  </button>
                </div>
              )
            ) : null,
        }}
      />
    </div>
  );
}
